/* Header custom element — handles sticky behavior on scroll and close dropdowns on ESC */
class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.isSticky = this.dataset.sticky === 'true';
    this.stickyHideOnScroll = this.dataset.stickyHide === 'true';
    this.isTransparent = this.dataset.transparent === 'true';
    this.schemeDefault = this.dataset.schemeDefault || 'scheme-1';
    this.schemeTransparent = this.dataset.schemeTransparent || this.schemeDefault;
    this.schemeSticky = this.dataset.schemeSticky || this.schemeDefault;
    this.lastScroll = 0;
    this.dropdowns = [...this.querySelectorAll('[data-nav-dropdown]')];

    if (this.isSticky || this.isTransparent) {
      this.refreshAnnouncementHeight();
      window.addEventListener('scroll', throttle(this.onScroll.bind(this), 100), { passive: true });
      window.addEventListener('resize', throttle(this.refreshAnnouncementHeight.bind(this), 200), { passive: true });
      // Recompute when the merchant adds, removes, or reorders sections
      // in the Theme Editor — Shopify fires these events on every change.
      // Without this, the transparent header keeps its initial offset
      // (which can be 0) and overlaps any newly-inserted topbar.
      const recompute = throttle(this.refreshAnnouncementHeight.bind(this), 100);
      document.addEventListener('shopify:section:load', recompute);
      document.addEventListener('shopify:section:unload', recompute);
      document.addEventListener('shopify:section:reorder', recompute);
    }

    this.dropdowns.forEach((dd) => {
      // Sync aria-expanded with the native `open` state via the `toggle` event.
      // The previous click-based logic computed the wrong value because it ran
      // AFTER the native toggle had already flipped the attribute.
      dd.addEventListener('toggle', () => {
        const summary = dd.querySelector('summary');
        summary?.setAttribute('aria-expanded', dd.open ? 'true' : 'false');
        // Fallback mutual-exclusion for browsers that don't support the
        // `name=""` ExclusiveAccordion semantics on <details> (Chrome <120,
        // Safari <17.4, Firefox <119). Modern browsers already do this natively.
        if (dd.open) this.closeAllDropdowns(dd);
      });
      dd.addEventListener('focusout', () => {
        setTimeout(() => {
          if (!dd.contains(document.activeElement)) dd.removeAttribute('open');
        });
      });
    });

    const nav = this.querySelector('.header__nav:not(.header__nav--drawer-desktop)');
    if (nav) this.navOverflow = new HeaderNavOverflow(nav);

    // Close any open dropdown when the user clicks outside the header nav.
    this.onDocumentClick = (e) => {
      if (this.contains(e.target)) return;
      this.closeAllDropdowns();
    };
    document.addEventListener('click', this.onDocumentClick);

    document.addEventListener('keyup', onKeyUpEscape);
    // Initial scheme application
    this.updateScheme();
  }

  updateScheme() {
    const scrolled = this.classList.contains('is-scrolled');
    let next = this.schemeDefault;
    if (this.isTransparent && !scrolled) {
      next = this.schemeTransparent;
    } else if (scrolled) {
      next = this.schemeSticky;
    }
    const prefix = 'color-';
    const target = prefix + next;
    const current = [...this.classList].find((c) => c.startsWith(prefix));
    if (current === target) return;
    if (current) this.classList.remove(current);
    this.classList.add(target);
  }

  refreshAnnouncementHeight() {
    // Sum the height of every bar rendered ABOVE the header — announcement bar,
    // topbar, scrolling promotion, or any combination. Their combined height is
    // the offset where the transparent/sticky header must sit so it never
    // overlaps them.
    //
    // We use the bars' own class selectors (reliable) and keep only the ones
    // positioned before the header in the document, via compareDocumentPosition.
    // This counts a scrolling promotion placed at the top of the page, while
    // correctly ignoring one placed lower down (e.g. mid-homepage).
    const ref = this.closest('[id^="shopify-section"]') || this;
    const selectors = ['.announcement-bar', '.topbar', '.scrolling-promotion'];
    let height = 0;
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        const precedesHeader =
          ref.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING;
        if (precedesHeader) height += el.offsetHeight || 0;
      });
    });
    this.announcementHeight = height;
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }

  onScroll() {
    const y = window.scrollY;
    // Trigger the "scrolled" state only after the announcement bar has scrolled out of view,
    // so the fixed/sticky header never overlaps it. Fall back to a small offset when there is no bar.
    const minOffset = this.isTransparent ? 60 : 16;
    const threshold = Math.max(this.announcementHeight || 0, minOffset);
    const wasScrolled = this.classList.contains('is-scrolled');
    this.classList.toggle('is-scrolled', y > threshold);
    const isScrolled = this.classList.contains('is-scrolled');
    if (wasScrolled !== isScrolled) this.updateScheme();
    // Auto-hide on scroll-down only if sticky, hide enabled, and not transparent
    if (this.isSticky && this.stickyHideOnScroll && !this.isTransparent) {
      const shouldHide = y > 120 && y > this.lastScroll;
      const shouldShow = y < this.lastScroll;
      if (shouldHide) this.classList.add('is-hidden');
      else if (shouldShow) this.classList.remove('is-hidden');
    }
    this.lastScroll = y;
  }

  closeAllDropdowns(except) {
    this.dropdowns.forEach((dd) => {
      if (dd === except || (except && dd.contains(except))) return;
      dd.removeAttribute('open');
      dd.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }
}

/* ============================================================
 * Desktop nav overflow — move items that don't fit into "More"
 * ============================================================ */
class HeaderNavOverflow {
  constructor(nav) {
    this.nav = nav;
    this.list = nav.querySelector('[data-nav-list]');
    this.overflowItem = nav.querySelector('[data-nav-overflow]');
    this.overflowList = nav.querySelector('[data-nav-overflow-list]');
    if (!this.list || !this.overflowItem || !this.overflowList) return;

    this.items = [...this.list.querySelectorAll(':scope > .header__nav-item:not([data-nav-overflow])')];
    if (this.items.length === 0) return;

    this.boundRecalculate = () => {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.recalculate());
    };
    this.resizeObserver = new ResizeObserver(this.boundRecalculate);
    this.resizeObserver.observe(this.nav);
    window.addEventListener('resize', this.boundRecalculate, { passive: true });

    const run = () => this.recalculate();
    if (document.fonts?.ready) document.fonts.ready.then(run);
    run();
  }

  get gap() {
    const value = Number.parseFloat(getComputedStyle(this.list).columnGap);
    return Number.isFinite(value) ? value : 0;
  }

  setNested(item, nested) {
    item.querySelectorAll('details[data-nav-dropdown]').forEach((dd) => {
      dd.setAttribute('name', nested ? 'header-overflow-dropdown' : 'header-dropdown');
      if (!nested) dd.removeAttribute('open');
    });
  }

  reset() {
    this.overflowItem.querySelector('details')?.removeAttribute('open');
    this.items.forEach((item) => {
      this.setNested(item, false);
      this.list.insertBefore(item, this.overflowItem);
    });
    this.overflowItem.hidden = true;
  }

  measureOverflowWidth() {
    const wasHidden = this.overflowItem.hidden;
    this.overflowItem.hidden = false;
    const width = this.overflowItem.offsetWidth;
    this.overflowItem.hidden = wasHidden;
    return width;
  }

  recalculate() {
    if (!this.items.length) return;
    if (this.nav.clientWidth === 0) {
      this.reset();
      return;
    }

    this.reset();

    const available = this.list.clientWidth;
    const gap = this.gap;
    let used = 0;
    this.items.forEach((item, index) => {
      used += item.offsetWidth + (index > 0 ? gap : 0);
    });

    if (used <= available + 1) return;

    const overflowBudget = this.measureOverflowWidth() + gap;
    let width = 0;
    let firstHidden = this.items.length;

    for (let i = 0; i < this.items.length; i += 1) {
      const next = width + this.items[i].offsetWidth + (i > 0 ? gap : 0);
      if (next + overflowBudget > available + 1) {
        firstHidden = i;
        break;
      }
      width = next;
    }

    if (firstHidden === 0) firstHidden = 1;

    if (firstHidden < this.items.length) {
      this.overflowItem.hidden = false;
      for (let i = firstHidden; i < this.items.length; i += 1) {
        this.setNested(this.items[i], true);
        this.overflowList.appendChild(this.items[i]);
      }
    }
  }
}

customElements.define('lion-header-component', HeaderComponent);

/* ============================================================
 * <lion-mobile-menu> — accessible slide-in drawer with focus trap
 * ============================================================ */
class MobileMenu extends HTMLElement {
  constructor() {
    super();
    this.panel = this.querySelector('.mobile-menu__panel');
    this.openers = [...document.querySelectorAll('[data-mobile-menu-open]')];
    this.closers = [...this.querySelectorAll('[data-mobile-menu-close]')];
    this.isOpen = false;
    this.previouslyFocused = null;

    this.openers.forEach((btn) => btn.addEventListener('click', () => this.open()));
    this.closers.forEach((btn) => btn.addEventListener('click', () => this.close()));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  open() {
    if (this.isOpen) return;
    this.previouslyFocused = document.activeElement;
    this.removeAttribute('hidden');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => this.classList.add('is-open'));
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'true'));
    if (typeof window.trapFocus === 'function') window.trapFocus(this.panel);
    this.isOpen = true;
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.openers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('overflow-hidden');
    if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.previouslyFocused);
    // Wait for CSS transition to finish before hiding
    setTimeout(() => { if (!this.classList.contains('is-open')) this.setAttribute('hidden', ''); }, 320);
    this.isOpen = false;
  }
}
if (!customElements.get('lion-mobile-menu')) customElements.define('lion-mobile-menu', MobileMenu);
